import React, { useMemo } from 'react';
import * as THREE from 'three';

interface SandpaperProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Hex colour for the abrasive face. Default is a brown sand colour. */
  gritColor?: string;
  /** Width/length/height of the pad. */
  size?: [number, number, number];
}

/**
 * Sandpaper pad — a flexible piece of sandpaper used to remove the oxide
 * layer from metal samples like magnesium ribbon. Modelled as a thin
 * slightly curved card with grit speckles on the top face and a paper
 * backing on the underside.
 */
export function Sandpaper({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  gritColor = '#b87a3a',
  size = [0.18, 0.005, 0.10],
}: SandpaperProps) {
  const [w, h, d] = size;

  // Deterministic grit positions so the speckles don't jitter between
  // renders. Use a small PRNG seeded by index.
  const grits = useMemo(() => {
    const count = 90;
    const out: { x: number; z: number; r: number; tint: number }[] = [];
    let seed = 137;
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    for (let i = 0; i < count; i++) {
      out.push({
        x: (rand() - 0.5) * (w - 0.01),
        z: (rand() - 0.5) * (d - 0.01),
        r: 0.0008 + rand() * 0.0012,
        tint: rand(),
      });
    }
    return out;
  }, [w, d]);

  const darkGrit = new THREE.Color(gritColor).multiplyScalar(0.55).getHexString();

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Paper backing (thicker, slightly off-white) */}
      <mesh position={[0, -h / 4, 0]} receiveShadow castShadow>
        <boxGeometry args={[w, h / 2, d]} />
        <meshStandardMaterial color="#e6dfc8" roughness={0.95} />
      </mesh>
      {/* Abrasive top layer */}
      <mesh position={[0, h / 4 + 0.0005, 0]} receiveShadow>
        <boxGeometry args={[w * 0.985, h / 2, d * 0.985]} />
        <meshStandardMaterial color={gritColor} roughness={0.98} metalness={0.0} />
      </mesh>
      {/* Grit speckles — small darker dots scattered across the abrasive face */}
      {grits.map((g, i) => (
        <mesh key={i} position={[g.x, h / 2 + 0.0008, g.z]}>
          <sphereGeometry args={[g.r, 6, 6]} />
          <meshStandardMaterial color={`#${g.tint > 0.6 ? darkGrit : '6b4423'}`} roughness={1} />
        </mesh>
      ))}
      {/* Slightly worn corners */}
      <mesh position={[-w / 2 + 0.005, 0, -d / 2 + 0.004]} rotation={[0, Math.PI / 4, 0]}>
        <boxGeometry args={[0.012, h * 1.02, 0.004]} />
        <meshStandardMaterial color="#9d6a30" roughness={0.95} />
      </mesh>
    </group>
  );
}

export default Sandpaper;
